import React, { FC } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import Col from 'react-bootstrap/Col';
import API from 'core/Api';
import { setAuthTokensFromResponse } from 'utils/auth';
import { parseAndStoreUserData } from 'utils/userDataSync';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { useWorkModeContext } from 'store/work-mode';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from 'store/language';
import { useTwoFactorContext } from 'store/two-factor';

interface IProps {
  goTo2FALogin: () => void;
}

interface IInputs {
  email: string;
  password: string;
}

const LoginForm: FC<IProps> = ({ goTo2FALogin }: IProps) => {
  const { t } = useTranslation();
  const { setPermissions } = usePermissionsContext();
  const { setWorkMode, setWorkModeName, setAltWorkModeName } = useWorkModeContext();
  const { setLanguage } = useLanguageContext();
  const { set2FaKeys } = useTwoFactorContext();
  const location = useLocation<{ from?: { pathname: string } }>();
  const history = useHistory();
  const methods = useForm<IInputs>();
  const onSubmit = (data: IInputs) => {
    return API.post('auth/login', data)
      .then(async res => {
        const { continue_to_page: continueTo } = res.data;

        // Set tokens
        await setAuthTokensFromResponse(res);

        const permissions = await API.get('permissions');
        setPermissions(permissions.data);

        // Parse and store user data
        parseAndStoreUserData(res.data, {
          setWorkMode,
          setWorkModeName,
          setAltWorkModeName,
          setLanguage,
        });

        // 2FA is complete if 2FA flow is disabled OR user has set 2FA
        set2FaKeys({
          is2FaEnabled: continueTo !== 'home',
          is2FaComplete: continueTo !== '2fa-set',
        });

        // If 2FA flow is disabled
        if (continueTo === 'home') {
          const from = location.state?.from!.pathname;
          return history.replace(from ?? '/home');
        }

        // If 2FA flow is enabled but user 2FA is NOT set
        if (continueTo === '2fa-set') return history.push('/profile/2fa-activate');

        // If 2FA flow is enabled and user 2FA IS set
        if (continueTo === '2fa-login') return goTo2FALogin();
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, methods.setError));
  };
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      btnLabel={t('auth.login.login')}
      hideCancelBtn
    >
      <FormGroup
        controlId="formGroupEmail"
        label={t('auth.login.email')}
        type="email"
        placeholder={t('auth.login.emailPlaceholder')}
        name="email"
        isRequired
      />
      <FormGroup
        controlId="formGroupPassword"
        label={t('auth.login.pass')}
        type="password"
        placeholder={t('auth.login.passPlaceholder')}
        name="password"
        isPassword
        isRequired
      >
        <Col className="text-right">
          <Link
            id="forgotten-password"
            to="/forgotten-password"
          >
            {t('auth.login.forgottenPassword')}
          </Link>
        </Col>
      </FormGroup>
    </Form>
  );
};

export default LoginForm;
